import React from 'react'
import { TAutoComplete, TAutoCompletes, IAutoCompleteItem } from './interface'
import {
  defaults,
  dataFetcher,
  validateAutoCompleteData,
  END_POINTS,
} from '../../Utils/utils'
import { IAutoCompleteResponse } from '../../Utils/interface'

export const getAutoCompletes = async (props: {
  baseURL: string
  searchTerm: string
  contentType: string | null
  callBack: Function
  pageId?: string
}) => {
  const { baseURL = '', searchTerm, contentType, callBack, pageId } = props
  if (searchTerm.length >= defaults.minSearchString) {
    const body: {
      Term: string
      Group: string
      Filters: never[]
      PageId?: string
    } = {
      Term: searchTerm,
      Group: contentType || defaults.group,
      Filters: [],
    }
    if (pageId) {
      body.PageId = pageId
    }
    const response: IAutoCompleteResponse = await dataFetcher(
      `${baseURL}${END_POINTS.autocomplete}`,
      body
    )
    if (response?.results) {
      const validData = validateAutoCompleteData(response)
      const aggregatedAutocompleteItems: {
        items: IAutoCompleteItem[]
        suggestions: string[]
      } = { items: [], suggestions: [] }
      if (validData.results) {
        aggregatedAutocompleteItems.items?.push(...response.results)
      }
      if (validData.suggestions) {
        aggregatedAutocompleteItems.suggestions?.push(...response.suggestions)
      }
      if (
        aggregatedAutocompleteItems.items.length > 0 ||
        aggregatedAutocompleteItems.suggestions.length > 0
      ) {
        callBack(aggregatedAutocompleteItems)
      } else {
        callBack([])
      }
    }
  } else {
    callBack(undefined)
  }
}

/**
 * This breaks up the text to insert a <span /> surrounding the martched "search term"
 * @param props Object
 * @returns JSX
 */
const AutoComplete = (props: TAutoComplete) => {
  const { searchTerm, item } = props
  const regex = new RegExp(searchTerm, 'ig')
  const split = item.split(regex)
  const matches = item.match(regex) || []
  const result: React.JSX.Element[] = []
  for (let i = 0; i <= split.length; i += 1) {
    result.push(split[i] as unknown as React.JSX.Element)
    if (matches[i]) {
      result.push(
        <span key={i} className='autocompletes__match'>
          {matches[i]}
        </span>
      )
    }
  }
  return <span>{result}</span>
}

const AutoCompletes = (props: TAutoCompletes) => {
  const {
    searchTerm,
    items,
    suggestions,
    suggestionsCallBack,
    useAsSearch,
    itemsCallBack,
  } = props

  return (
    <div className='autocompletes__list-wrapper'>
      <ul
        id='suggestions-list'
        role='listbox'
        aria-labelledby='global-search'
        className='autocompletes__list'
        tabIndex={2}>
        {items.slice(0, 3).map((item, index) => {
          return (
            <li
              role='option'
              className='autocompletes__item'
              key={`${index}-${item.title}`}>
              {useAsSearch && itemsCallBack ? (
                <a
                  href='#'
                  onClick={(e) => {
                    e.preventDefault()
                    itemsCallBack(item.title)
                  }}
                  className='autocompletes__link link'>
                  <AutoComplete searchTerm={searchTerm} item={item.title} />
                  {item.contentType && (
                    <span className='autocompletes__type'>
                      {item.contentType}
                    </span>
                  )}
                </a>
              ) : (
                <a href={item.url} className='autocompletes__link link'>
                  <AutoComplete searchTerm={searchTerm} item={item.title} />
                  {item.contentType && (
                    <span className='autocompletes__type'>
                      {item.contentType}
                    </span>
                  )}
                </a>
              )}
            </li>
          )
        })}
        {suggestions.map((suggestion, index) => {
          return (
            <li
              role='option'
              className='autocompletes__item'
              key={`${index}-suggestion-${suggestion}`}>
              <a
                href='#'
                onClick={(e) => {
                  e.preventDefault()
                  suggestionsCallBack(suggestion)
                }}
                className='autocompletes__link link'>
                <span>
                  <AutoComplete searchTerm={searchTerm} item={suggestion} />
                  ...
                </span>
              </a>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default AutoCompletes
